import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AddEngagementPopup.module.scss";
import Button from "../../../../common/Button/Button";
import Dropdown from "../../../../common/Dropdown/Dropdown";
import DatePicker from "../../../../common/DatePicker/DatePicker";
import UploadInput, {
  UploadedFile,
} from "../../../../common/UploadInput/UploadInput";
import InputField from "../../../../common/InputField/InputField";
import FormatterBar from "../../../../common/FormatterBar/FormatterBar";
import DropdownMultiselect from "../../../../common/DropdownMultiselect/DropdownMultiselect";
import { DropdownItem } from "../../../../common/DropdownMultiselect/DropdownMultiselect";
import { useClearErrorOnChange } from "../../../../CustomHooks/useClearErrorOnChange";
import {
  typeOptions,
  channelOptions,
} from "../../../../../constants/dataConstants";
import { useProfileParams } from "../../../../../utils/common";
import { goalsAPI } from "../../../../../redux/services/goalsAPI";
import { useAppSelector } from "../../../../../redux/hooks";

interface AddEngagementPopupProps {
  goalID?: number | null | undefined;
  onSave: (engagementData: any) => void;
  onCancel: () => void;
  initialData?: {
    title?: string;
    type?: { code: number; value: string };
    channel?: { code: number; value: string };
    linkedGoals?: number[];
    date?: Date | string;
    description?: string;
    attachments?: UploadedFile[];
  };
}

const AddEngagementPopup: React.FC<AddEngagementPopupProps> = ({
  goalID,
  onSave,
  onCancel,
  initialData,
}) => {
  const { t } = useTranslation();
  const findDropdownItem = (
    options: DropdownItem[],
    value?: string
  ): DropdownItem | undefined => options.find((o) => o.value === value);
  const translatedChannelOptions = channelOptions.map((opt) => ({
    code: opt.code,
    value: t(`ChannelOptions.${opt.value}`),
  }));

  const translatedTypeOptions = typeOptions.map((opt) => ({
    code: opt.code,
    value: t(`TypeOptions.${opt.value}`),
  }));
  const [engagementTitle, setEngagementTitle] = useState(
    initialData?.title || ""
  );
  const [selectedType, setSelectedType] = useState<DropdownItem | undefined>(
    findDropdownItem(
      translatedTypeOptions,
      t(`TypeOptions.${initialData?.type?.value}`)
    )
  );

  const [selectedChannel, setSelectedChannel] = useState<
    DropdownItem | undefined
  >(
    findDropdownItem(
      translatedChannelOptions,
      t(`ChannelOptions.${initialData?.channel?.value}`)
    )
  );
  const [selectedGoals, setSelectedGoals] = useState<DropdownItem[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialData?.date ? new Date(initialData.date) : undefined
  );
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [attachments, setAttachments] = useState<UploadedFile[]>(
    initialData?.attachments || []
  );
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );

  const { affiliateCode, aboNumber } = useProfileParams();
  const goalsData = useAppSelector(
    (state) =>
      goalsAPI.endpoints.getGoalsFullData.select({ affiliateCode, aboNumber })(
        state
      )?.data
  );

  const goalOptions: DropdownItem[] = useMemo(() => {
    if (!Array.isArray(goalsData?.body)) return [];
    return goalsData.body.map((goal: any) => ({
      code: goal.aboGoalId,
      value: goal.title,
    }));
  }, [goalsData?.body]);

  // select based on goalId
  useEffect(() => {
    if (!goalID || !goalOptions.length) return;
    if (!selectedGoals.length) {
      const preSelect = goalOptions.find((goal) => goal.code === goalID);
      if (preSelect) setSelectedGoals([preSelect]);
    }
  }, [goalOptions, goalID, selectedGoals]);

  /* Sync initial data with form state when Editing engagement */
  useEffect(() => {
    if (!initialData) return;

    setEngagementTitle(initialData.title || "");
    setSelectedType(
      findDropdownItem(
        translatedTypeOptions,
        t(`TypeOptions.${initialData.type?.value}`)
      )
    );
    setSelectedChannel(
      findDropdownItem(
        translatedChannelOptions,
        t(`ChannelOptions.${initialData.channel?.value}`)
      )
    );
    if (Array.isArray(initialData.linkedGoals) && initialData.linkedGoals.length) {
      const preselected = goalOptions.filter((g) =>
        initialData.linkedGoals?.includes(Number(g.code))
      );
      setSelectedGoals(preselected);
    } else {
      setSelectedGoals([]);
    }
    setSelectedDate(initialData.date ? new Date(initialData.date) : undefined);
    setDescription(initialData.description || "");
    // setAttachments(initialData.attachments || []);
    setAttachments(
      (initialData.attachments || []).map((file) => ({
        ...file,
        isExisting: true,
      }))
    );
  }, [initialData, goalOptions]);

  useClearErrorOnChange(engagementTitle, "engagementTitle", errors, setErrors);
  useClearErrorOnChange(selectedType, "type", errors, setErrors);
  useClearErrorOnChange(selectedChannel, "channel", errors, setErrors);
  useClearErrorOnChange(selectedDate, "date", errors, setErrors);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!engagementTitle) {
      newErrors.engagementTitle = t("addEngagementTitleRequired");
    }

    if (!selectedType) {
      newErrors.type = t("addEngagementTypeRequired");
    }

    if (!selectedChannel) {
      newErrors.channel = t("addEngagementChannelRequired");
    }

    if (!selectedDate) {
      newErrors.date = t("addEngagementDateRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const aboGoalIds = selectedGoals.map((g) => Number(g.code));

    const engagementData = {
      title: engagementTitle,
      type: Number(selectedType?.code),
      channel: Number(selectedChannel?.code),
      aboGoalIds,
      date: selectedDate,
      description: description,
      attachments: attachments,
    };

    onSave(engagementData);
    onCancel();
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
  };

  const handleAttachmentsChange = (files: UploadedFile[]) => {
    setAttachments(
      files.map((f) => ({
        ...f,
        isExisting: f.isExisting ?? false,
      }))
    );
  };

  const handleGoalChange = (selectedItems: DropdownItem[]) => {
    setSelectedGoals(selectedItems);
  };

  return (
    <div className={styles.engagement_popup_container}>
      <h2 className={styles.header_title}>
        {t(initialData ? "editEngagement" : "addEngagementModalTitle")}
      </h2>
      <div className={styles.form_content}>
        {/* Engagement Title */}
        <InputField
          label={t("engagementTitle") + "*"}
          value={engagementTitle}
          onChange={setEngagementTitle}
          maxLength={50}
          size="md"
          invalid={!!errors.engagementTitle}
          errorMsg={errors.engagementTitle}
        />

        {/* Type and Channel Row */}
        <div className={styles.form_row}>
          <Dropdown
            dropdownName={t("engagementType") + "*"}
            items={translatedTypeOptions}
            selectedItem={selectedType}
            handleSelectItemCallback={setSelectedType}
            size="md"
            invalid={!!errors.type}
            errorMsg={errors.type}
          />
          <Dropdown
            dropdownName={t("engagementChannel") + "*"}
            items={translatedChannelOptions}
            selectedItem={selectedChannel}
            handleSelectItemCallback={setSelectedChannel}
            size="md"
            invalid={!!errors.channel}
            errorMsg={errors.channel}
          />
        </div>

        {/* Linked Goal */}
        <DropdownMultiselect
          dropdownName={t("linkedGoal")}
          items={goalOptions}
          selectedItems={selectedGoals}
          handleSelectItemCallback={handleGoalChange}
          addScroll
        />

        {/* Date */}
        <DatePicker
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          size="md"
          placeholder={t("date") + "*"}
          invalid={!!errors.date}
          errorMsg={errors.date}
        />

        {/* Add Attachments */}
        <UploadInput
          onFilesChange={handleAttachmentsChange}
          placeholder={t("addAttachments")}
          size="md"
          maxFiles={5}
          initialFiles={attachments}
        />

        {/* Description */}
        <FormatterBar
          id="description"
          testId="description_testID"
          editorData={description}
          handleDataChange={handleDescriptionChange}
          section={"description"}
          placeholder={t("addDescription")}
          isContentEditable={true}
          maxCharacter={1000}
        />
      </div>

      {/* Action Buttons */}
      <div className={styles.action_buttons}>
        <Button
          label={t("btnCancel")}
          onClick={onCancel}
          className={styles.cancel_btn}
        />

        <Button
          label={t("btnSave")}
          onClick={handleSave}
          className={styles.save_btn}
          style="filled"
        />
      </div>
    </div>
  );
};

export default AddEngagementPopup;
